import React from "react";
import { motion } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { useTranslation } from "react-i18next";
import { Star, ArrowRight, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { ASSETS } from "../lib/constants";
import { Link } from "react-router-dom";
import HeroPixelBg from "./HeroPixelBg";

export default function Hero({ onBookNow }) {
  const { t, i18n } = useTranslation();
  const phrases = t("hero.typed", { returnObjects: true });
  const sequence = phrases.flatMap((p) => [p, 1800]);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-brand-black grain"
    >
      {/* faint grid background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#0A0A0A 1px, transparent 1px), linear-gradient(90deg, #0A0A0A 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Pixel-block animated black + orange slabs (boxing in toward centre) */}
      <HeroPixelBg />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 sm:px-6 md:grid-cols-12 md:py-20 lg:px-10 lg:py-24">
        {/* LEFT — branded hero image with glowing pulse behind */}
        <div className="relative col-span-1 hidden md:col-span-5 md:block">
          {/* pulsing orange glow */}
          <motion.div
            aria-hidden="true"
            animate={{ opacity: [0.45, 0.85, 0.45], scale: [0.92, 1.04, 0.92] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="pointer-events-none absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-orange/40 blur-[80px]"
          />
          <motion.img
            src={ASSETS.heroLogo}
            alt="Cascante Moto Rental — Your best option in town"
            initial={{ x: -80, opacity: 0, scale: 0.94 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[560px] drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
          />
          {/* speed lines */}
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: [0, 1, 0], x: [-50, 50, 150] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-2 left-0 flex flex-col gap-2"
          >
            <div className="h-[3px] w-24 bg-brand-orange" />
            <div className="h-[3px] w-16 bg-white" />
            <div className="h-[3px] w-32 bg-brand-orange" />
          </motion.div>
        </div>

        {/* RIGHT — copy + CTA */}
        <div className="col-span-1 flex flex-col justify-center md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 inline-flex w-fit items-center gap-2 border border-white/15 bg-white/10 px-3 py-1.5 backdrop-blur"
          >
            <span className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-brand-orange text-brand-orange" />
              ))}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white">
              {t("hero.eyebrow")}
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl"
          >
            <TypeAnimation
              key={i18n.language}
              sequence={sequence}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              cursor={true}
              className="text-white"
            />
            <span className="block text-stroke">San Juan del Sur</span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white/85 sm:text-lg"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Button
              data-testid="hero-book-now-btn"
              onClick={onBookNow}
              className="group h-14 rounded-none bg-brand-orange px-8 font-display text-2xl uppercase tracking-wider text-white hover:bg-white hover:text-brand-orange"
            >
              {t("hero.bookNow")}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Link
              to="/rentals"
              data-testid="hero-view-motos-link"
              className="group inline-flex h-14 items-center gap-2 border-2 border-white/40 bg-transparent px-6 font-display text-xl uppercase tracking-wider text-white hover:border-brand-orange hover:text-brand-orange"
            >
              {t("hero.viewMotos")}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-bold uppercase tracking-[0.2em] text-white md:text-white"
          >
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" /> Rivas, Nicaragua
            </div>
            <div>{t("proof.reviews")}</div>
            <div>{t("proof.rating")}</div>
          </motion.div>
        </div>
      </div>

      {/* mobile-only hero image */}
      <div className="relative -mt-4 px-4 pb-8 md:hidden">
        <motion.img
          src={ASSETS.heroLogo}
          alt="Cascante Moto Rental"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.9 }}
          className="w-full drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
        />
      </div>
    </section>
  );
}
